import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { FilePlus, Plus, Search, X } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { BrandModelOption, BrandOption, ParentTicketFormValues } from '@/types/ticket'
import { apiClient } from '@/lib/api/client'
import { CreateAssetModal } from './CreateAssetModal'

type TicketAsset = NonNullable<ParentTicketFormValues['assets']>[number]

type Props = {
  assets: TicketAsset[]
  onChange: (assets: TicketAsset[]) => void
  clientCode?: string
  storeId?: number | null
  storeName?: string | null
  isReadOnly?: boolean
}

export function TicketAssetsEditor({
  assets,
  onChange,
  clientCode,
  storeId,
  storeName,
  isReadOnly = false,
}: Props) {
  const colors = useThemeColors()
  const { showError } = useToastNotification()
  const { isOpen: isAssetModalOpen, onOpen: onAssetModalOpen, onClose: onAssetModalClose } = useDisclosure()
  const {
    isOpen: isCreateAssetModalOpen,
    onOpen: onCreateAssetModalOpen,
    onClose: onCreateAssetModalClose,
  } = useDisclosure()

  const [assetSearchTerm, setAssetSearchTerm] = useState('')
  const [assetSearchResults, setAssetSearchResults] = useState<TicketAsset[]>([])
  const [isAssetSearching, setIsAssetSearching] = useState(false)
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([])
  const [brandModelsByBrandId, setBrandModelsByBrandId] = useState<Record<number, BrandModelOption[]>>({})
  const assetSearchInputRef = useRef<HTMLInputElement>(null)
  const assetSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    let cancelled = false
    apiClient
      .get('/options/brands')
      .then((res) => {
        if (cancelled) return
        const rows = Array.isArray(res.data) ? res.data : []
        setBrandOptions(
          rows.map((row: { id: number; name?: string }) => ({
            id: Number(row.id),
            name: String(row.name ?? row.id),
          })),
        )
      })
      .catch(() => {
        if (!cancelled) setBrandOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadBrandModels = useCallback(async (brandId: number) => {
    if (brandModelsByBrandId[brandId]) return
    try {
      const res = await apiClient.get('/options/brand-models', { params: { brand_id: brandId } })
      const rows = Array.isArray(res.data) ? res.data : []
      setBrandModelsByBrandId((prev) => ({
        ...prev,
        [brandId]: rows.map((row: { id: number; name?: string; brand_id?: number }) => ({
          id: Number(row.id),
          name: String(row.name ?? row.id),
          brand_id: row.brand_id != null ? Number(row.brand_id) : brandId,
        })),
      }))
    } catch {
      setBrandModelsByBrandId((prev) => ({ ...prev, [brandId]: [] }))
    }
  }, [brandModelsByBrandId])

  const updateAsset = (assetId: number, patch: Partial<TicketAsset>) => {
    onChange(assets.map((asset) => (asset.id === assetId ? { ...asset, ...patch } : asset)))
  }

  const findBrandIdByName = (name?: string | null) =>
    brandOptions.find((b) => b.name === name)?.id

  const renderBrandCell = (asset: TicketAsset) => {
    if (isReadOnly) {
      return (
        <Text fontSize="sm" color={colors.text}>
          {asset.brand_name || '-'}
        </Text>
      )
    }
    const brandId = findBrandIdByName(asset.brand_name)
    return (
      <Select
        size="sm"
        placeholder="Select brand"
        value={brandId ?? ''}
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : undefined
          const brand = brandOptions.find((b) => b.id === id)
          updateAsset(asset.id, { brand_name: brand?.name ?? '', model: '' })
          if (id) void loadBrandModels(id)
        }}
        {...inputBaseProps}
      >
        {brandOptions.map((brand) => (
          <option key={brand.id} value={brand.id}>{brand.name}</option>
        ))}
      </Select>
    )
  }

  const renderModelCell = (asset: TicketAsset) => {
    if (isReadOnly) {
      return (
        <Text fontSize="sm" color={colors.text}>
          {asset.model || '-'}
        </Text>
      )
    }
    const brandId = findBrandIdByName(asset.brand_name)
    const models = brandId ? brandModelsByBrandId[brandId] ?? [] : []
    if (brandId && !brandModelsByBrandId[brandId]) {
      void loadBrandModels(brandId)
    }
    const modelId = models.find((m) => m.name === asset.model)?.id
    return (
      <Select
        size="sm"
        placeholder="Select model"
        value={modelId ?? ''}
        isDisabled={!brandId}
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : undefined
          const model = models.find((m) => m.id === id)
          updateAsset(asset.id, { model: model?.name ?? '' })
        }}
        {...inputBaseProps}
      >
        {models.map((model) => (
          <option key={model.id} value={model.id}>{model.name}</option>
        ))}
      </Select>
    )
  }

  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    _hover: { borderColor: colors.inputborderHover ?? colors.inputBorder ?? 'gray.300' },
    _focusVisible: {
      borderColor: colors.primary ?? 'blue.500',
      boxShadow: `0 0 0 1px ${colors.primary ?? '#3182ce'}`,
    },
  } as const

  const searchAssets = useCallback(
    async (searchTerm: string) => {
      if (searchTerm.length < 3) {
        setAssetSearchResults([])
        return
      }

      setIsAssetSearching(true)
      try {
        const response = await TicketService.searchAssets(
          searchTerm,
          clientCode,
          storeId ?? undefined,
        )
        if (response.success && response.data) {
          setAssetSearchResults(response.data)
        } else {
          setAssetSearchResults([])
          if (response.message && !response.message.includes('No assets found')) {
            showError(response.message)
          }
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to search assets'
        showError(message)
        setAssetSearchResults([])
      } finally {
        setIsAssetSearching(false)
      }
    },
    [clientCode, showError, storeId],
  )

  const handleAssetSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAssetSearchTerm(value)

    if (assetSearchTimeoutRef.current) {
      clearTimeout(assetSearchTimeoutRef.current)
    }

    assetSearchTimeoutRef.current = setTimeout(() => {
      searchAssets(value)
    }, 300)
  }

  const handleAssetSelect = (asset: TicketAsset) => {
    if (assets.some((a) => a.id === asset.id)) {
      return
    }
    onChange([...assets, asset])
  }

  const handleRemoveAsset = (assetId: number) => {
    onChange(assets.filter((a) => a.id !== assetId))
  }

  const handleSelectAllVisible = (checked: boolean) => {
    if (checked) {
      const newAssets = assetSearchResults.filter((asset) => !assets.some((a) => a.id === asset.id))
      onChange([...assets, ...newAssets])
    } else {
      onChange(assets.filter((asset) => !assetSearchResults.some((a) => a.id === asset.id)))
    }
  }

  return (
    <>
      <FormControl w="100%">
        {!isReadOnly && (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            w="full"
            onClick={onAssetModalOpen}
            leftIcon={<Icon as={Plus as any} boxSize="16px" />}
          >
            Add Asset
          </Button>
        )}
        {assets.length > 0 && (
          <Box
            mt={isReadOnly ? 0 : 3}
            border="1px solid"
            borderColor={colors.borderLight}
            borderRadius="md"
            overflow="hidden"
          >
            <Table size="sm" variant="simple">
              <Thead bg={colors.cardBg}>
                <Tr>
                  {!isReadOnly && <Th width="50px"></Th>}
                  <Th>Tag Number</Th>
                  <Th>Model</Th>
                  <Th>Serial Number</Th>
                  <Th>Brand</Th>
                  <Th>Category</Th>
                  <Th>Function</Th>
                  <Th>Warranty Date</Th>
                  <Th>Warranty Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {assets.map((asset) => (
                  <Tr key={asset.id} _hover={{ bg: 'gray.50' }}>
                    {!isReadOnly && (
                      <Td>
                        <IconButton
                          aria-label="Remove asset"
                          icon={<X size={16} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleRemoveAsset(asset.id)}
                        />
                      </Td>
                    )}
                    <Td>
                      <Text fontWeight="600" fontSize="sm" color={colors.text}>
                        {asset.tag_number || '-'}
                      </Text>
                    </Td>
                    <Td>
                      {renderModelCell(asset)}
                    </Td>
                    <Td>
                      <Text fontSize="sm" color={colors.text}>
                        {asset.serial_number || '-'}
                      </Text>
                    </Td>
                    <Td>{renderBrandCell(asset)}</Td>
                    <Td>
                      <Text fontSize="sm" color={colors.text}>
                        {asset.category_name || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color={colors.text}>
                        {asset.function || '-'}
                      </Text>
                    </Td>
                    <Td>
                      {isReadOnly ? (
                        <Text fontSize="sm" color={colors.text}>{asset.warranty_date || '-'}</Text>
                      ) : (
                        <Input
                          size="sm"
                          type="date"
                          value={asset.warranty_date ?? ''}
                          onChange={(e) => updateAsset(asset.id, { warranty_date: e.target.value || null })}
                          {...inputBaseProps}
                        />
                      )}
                    </Td>
                    <Td>
                      {isReadOnly ? (
                        <Text fontSize="sm" color={colors.text}>{asset.warranty_status || '-'}</Text>
                      ) : (
                        <Input
                          size="sm"
                          value={asset.warranty_status ?? ''}
                          onChange={(e) => updateAsset(asset.id, { warranty_status: e.target.value || null })}
                          placeholder="e.g. Active"
                          {...inputBaseProps}
                        />
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
        {!isReadOnly && (
          <FormHelperText fontSize="xs" color={colors.textMuted} mt={1} mb={0}>
            Leave blank if no item needs to be added.
          </FormHelperText>
        )}
      </FormControl>

      {!isReadOnly && (
        <>
          <Modal isOpen={isAssetModalOpen} onClose={onAssetModalClose} size="6xl">
            <ModalOverlay />
            <ModalContent maxW="90vw">
              <ModalHeader>
                <HStack justify="space-between" align="center" width="100%" pr={10}>
                  <Text>Search Assets</Text>
                  <Button
                    size="sm"
                    colorScheme="green"
                    leftIcon={<Icon as={FilePlus as any} boxSize="16px" />}
                    onClick={onCreateAssetModalOpen}
                  >
                    Create New Asset
                  </Button>
                </HStack>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <InputGroup>
                      <Input
                        ref={assetSearchInputRef}
                        placeholder="Search by tag number, model, serial number, store code, store name..."
                        value={assetSearchTerm}
                        onChange={handleAssetSearchChange}
                        {...inputBaseProps}
                      />
                      <InputRightElement>
                        {isAssetSearching ? (
                          <Spinner size="sm" color={colors.primary} />
                        ) : (
                          <Search size={16} color={colors.textSecondary} />
                        )}
                      </InputRightElement>
                    </InputGroup>
                    <FormHelperText fontSize="xs" color={colors.textMuted} mt={1}>
                      Type at least 3 characters to search
                      {storeName && (
                        <Text as="span" fontWeight="600" ml={1}>
                          • Filtered by store: {storeName}
                        </Text>
                      )}
                    </FormHelperText>
                  </FormControl>

                  {assetSearchResults.length > 0 && (
                    <Box
                      border="1px solid"
                      borderColor={colors.borderLight}
                      borderRadius="md"
                      maxH="500px"
                      overflowY="auto"
                    >
                      <Table size="sm" variant="simple">
                        <Thead position="sticky" top={0} bg={colors.cardBg} zIndex={1}>
                          <Tr>
                            <Th width="50px">
                              <Checkbox
                                isChecked={
                                  assetSearchResults.length > 0 &&
                                  assetSearchResults.every((asset) => assets.some((a) => a.id === asset.id))
                                }
                                isIndeterminate={
                                  assetSearchResults.some((asset) => assets.some((a) => a.id === asset.id)) &&
                                  !assetSearchResults.every((asset) => assets.some((a) => a.id === asset.id))
                                }
                                onChange={(e) => handleSelectAllVisible(e.target.checked)}
                              />
                            </Th>
                            <Th>Tag Number</Th>
                            <Th>Model</Th>
                            <Th>Serial Number</Th>
                            <Th>Brand</Th>
                            <Th>Category</Th>
                            <Th>Function</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {assetSearchResults.map((asset) => {
                            const isSelected = assets.some((a) => a.id === asset.id)
                            return (
                              <Tr
                                key={asset.id}
                                _hover={{ bg: 'gray.50' }}
                                cursor="pointer"
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <Td onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    isChecked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation()
                                      if (e.target.checked) {
                                        handleAssetSelect(asset)
                                      } else {
                                        handleRemoveAsset(asset.id)
                                      }
                                    }}
                                  />
                                </Td>
                                <Td>
                                  <Text fontWeight="600" fontSize="sm" color={colors.text}>
                                    {asset.tag_number || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.model || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.serial_number || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.brand_name || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.category_name || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.function || '-'}
                                  </Text>
                                </Td>
                              </Tr>
                            )
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  )}

                  {assetSearchTerm.length >= 3 && assetSearchResults.length === 0 && !isAssetSearching && (
                    <Text fontSize="sm" color={colors.textSecondary} textAlign="center" py={4}>
                      No assets found matching &quot;{assetSearchTerm}&quot;
                    </Text>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <HStack spacing={2}>
                  <Text fontSize="sm" color={colors.textSecondary}>
                    {assets.length} asset{assets.length !== 1 ? 's' : ''} selected
                  </Text>
                  <Button colorScheme="blue" onClick={onAssetModalClose}>
                    Done
                  </Button>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <CreateAssetModal
            isOpen={isCreateAssetModalOpen}
            onClose={onCreateAssetModalClose}
            onSuccess={(newAsset) => {
              handleAssetSelect(newAsset)
              setAssetSearchResults((prev) => {
                if (prev.some((a) => a.id === newAsset.id)) {
                  return prev
                }
                return [newAsset, ...prev]
              })
              onCreateAssetModalClose()
            }}
            clientCode={clientCode}
          />
        </>
      )}
    </>
  )
}
